import { useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { useAuth } from '@/auth/context'
import { DEFAULT_HOME } from '@/auth/routes'
import { Button } from '@/ui/Button'
import { InlineError } from '@/ui/states'
import { PublicPage } from './PublicPage'
import { useToken } from './useToken'

/**
 * `/verify-email` — the link `verification_link()` builds.
 *
 * Public, because the address is confirmed by whoever holds the mailbox, and
 * they may be on a phone or in a browser with no session (`api/app/v1/auth.py`,
 * `verify_email`). Redeems with a **POST**, never from the `GET` that loaded the
 * page: mail security scanners fetch every URL in a message before a human
 * sees it, and this token is single-use.
 *
 * Four outcomes, all of them shown:
 *
 *  * **valid** — confirmed, and the user is sent on. Signed in, they land in
 *    the app; signed out, they are offered sign-in, because verifying an
 *    address does not create a session and pretending otherwise would leave
 *    them at a screen whose every button 401s.
 *  * **no token** — the link was truncated or edited.
 *  * **already used, expired, revoked** — one answer, deliberately. The API
 *    returns a single `VERIFICATION_FAILED` for all three, because the
 *    difference tells anyone holding a token which guess was closest.
 *  * **unreachable** — the network failure, with no request id to quote.
 */
export function VerifyEmail(): JSX.Element {
  const token = useToken()
  const navigate = useNavigate()
  const { status, refresh } = useAuth()

  const verify = useMutation((body: { token: string }) =>
    call(() => api.POST('/api/v1/auth/email/verify', { body })),
  )

  // React 19 StrictMode mounts effects twice in development. The token is
  // single-use, so a second POST would consume nothing (the first already
  // spent it) and then report failure over a success the user saw for one
  // frame. The ref makes the redemption happen once per page load, which is
  // what the user experiences anyway.
  const attempted = useRef(false)
  const { run } = verify
  useEffect(() => {
    if (!token || attempted.current) return
    attempted.current = true
    void (async () => {
      const result = await run({ token })
      if (result.ok && status === 'authenticated') await refresh()
    })()
  }, [token, run, status, refresh])

  if (!token) {
    return (
      <PublicPage title="That link is incomplete">
        <div className="ar-card__body ar-stack">
          <p style={{ marginTop: 0 }}>
            The address in your browser has no confirmation code in it. Mail clients sometimes cut
            long links across two lines — copying the whole thing usually fixes it.
          </p>
          <Link className="ar-btn ar-btn--primary" to="/login">
            Sign in and send a new link
          </Link>
        </div>
      </PublicPage>
    )
  }

  if (verify.state.status === 'done') {
    return (
      <PublicPage title="Address confirmed">
        <div className="ar-card__body ar-stack">
          <p className="ar-badge ar-badge--success" style={{ marginTop: 0 }}>
            <span aria-hidden="true">✓ </span>Your email address is confirmed
          </p>
          <p className="ar-muted">
            That is everything — you will get approval requests, digests and security alerts at this
            address.
          </p>
          {status === 'authenticated' ? (
            <Button
              tone="primary"
              onClick={() => {
                void navigate(DEFAULT_HOME)
              }}
            >
              Continue to AugRank
            </Button>
          ) : (
            <Link className="ar-btn ar-btn--primary" to="/login">
              Sign in
            </Link>
          )}
        </div>
      </PublicPage>
    )
  }

  if (verify.state.status === 'error') {
    return (
      <PublicPage title="That link no longer works">
        <div className="ar-card__body ar-stack">
          <InlineError failure={verify.state.failure} />
          <p className="ar-muted" style={{ marginTop: 0 }}>
            Confirmation links can be used once and expire. Sign in and we will send a fresh one —
            it takes a moment.
          </p>
          <Link className="ar-btn ar-btn--primary" to="/login">
            Sign in and send a new link
          </Link>
        </div>
      </PublicPage>
    )
  }

  return (
    <PublicPage title="Confirming your address">
      <div className="ar-card__body ar-row" aria-busy="true">
        <span className="ar-spinner" aria-hidden="true" />
        <span className="ar-muted">One moment.</span>
      </div>
    </PublicPage>
  )
}
